<template>
  <div class="chat-input pa-2">
    <!-- attachments preview -->
    <div v-if="pendingImage" class="mb-2">
      <v-chip close color="primary" outlined small @click:close="pendingImage = null">
        <v-icon size="14" left>mdi-image</v-icon>
        {{ pendingImage.name }}
      </v-chip>
    </div>
    <div v-if="pendingFile" class="mb-2">
      <v-chip close color="primary" outlined small @click:close="pendingFile = null">
        <v-icon size="14" left>mdi-paperclip</v-icon>
        {{ pendingFile.name }}
      </v-chip>
    </div>

    <!-- text + toolbar -->
    <div class="d-flex align-end">
      <!-- ⚡ Saved Replies (DB-backed) -->
      <SavedReplyMenu
        v-if="shopId"
        :shop-id="shopId"
        :staff-shop-id="staffShopId"
        @pick="onPickReply"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" :disabled="disabled" title="Saved Replies">
            <v-icon>mdi-lightning-bolt-outline</v-icon>
          </v-btn>
        </template>
      </SavedReplyMenu>

      <v-btn icon :disabled="disabled" :title="use_language.attach_image" @click="$refs.imageInput.click()">
        <v-icon>mdi-image-outline</v-icon>
      </v-btn>
      <input ref="imageInput" type="file" accept="image/*" hidden @change="onPickImage" />

      <v-btn icon :disabled="disabled" :title="use_language.attach_file" @click="$refs.fileInput.click()">
        <v-icon>mdi-paperclip</v-icon>
      </v-btn>
      <input ref="fileInput" type="file" hidden @change="onPickFile" />

      <v-menu offset-y top :close-on-content-click="true">
        <template v-slot:activator="{ on }">
          <v-btn icon :disabled="disabled" v-on="on" title="Emoji">
            <v-icon>mdi-emoticon-happy-outline</v-icon>
          </v-btn>
        </template>
        <v-card>
          <div class="emoji-grid pa-2">
            <span
              v-for="e in emojiList"
              :key="e"
              class="emoji-cell"
              @click="insertEmoji(e)"
            >{{ e }}</span>
          </div>
        </v-card>
      </v-menu>

      <v-textarea
        v-model="text"
        outlined
        dense
        hide-details
        rows="1"
        auto-grow
        :disabled="disabled"
        :placeholder="use_language.type_message + '... (Enter=' + use_language.send + ', Shift+Enter=' + use_language.new_line + ')'"
        class="ml-1"
        @keydown.enter.exact.prevent="onSend"
        @keydown.enter.shift.exact="() => {}"
      />

      <v-btn
        icon
        color="primary"
        :loading="sending"
        :disabled="!canSend"
        @click="onSend"
        :title="use_language.send"
      >
        <v-icon>mdi-send</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import SavedReplyMenu from "./SavedReplyMenu.vue";
import { get_languages } from '@/website/global_function'

const EMOJI_DEFAULT = [
  "😀","😁","😂","🤣","😊","😍","😘","🙂","🙃","😎",
  "🤔","😅","🥰","😢","😭","😡","😱","👍","👎","👌",
  "🙏","🎉","🔥","❤️","💯","✨","✅","❌","⭐","🛒",
];

export default {
  name: "ChatInputBar",
  components: { SavedReplyMenu },
  props: {
    disabled: { type: Boolean, default: false },
    sending: { type: Boolean, default: false },
    shopId: { type: [String, Number], default: null },
    staffShopId: { type: [String, Number], default: null },
  },
  data: () => ({
    use_language: {},
    text: "",
    pendingImage: null,
    pendingFile: null,
    emojiList: EMOJI_DEFAULT,
  }),
  computed: {
    canSend() {
      if (this.disabled || this.sending) return false;
      return !!(this.text.trim() || this.pendingImage || this.pendingFile);
    },
  },
  async created() {
    this.use_language = await this.get_languages(localStorage.getItem("set_language"));
  },
  methods: {
    get_languages,
    onSend() {
      if (!this.canSend) return;
      const payload = {
        text: this.text.trim(),
        image: this.pendingImage,
        file: this.pendingFile,
      };
      // ไม่ clear ทันที — รอ parent confirm ผ่าน clearInput()
      this.$emit("send", payload);
    },

    /** Public method: เรียกจาก parent หลัง send สำเร็จ */
    clearInput() {
      this.text = "";
      this.pendingImage = null;
      this.pendingFile = null;
      if (this.$refs.imageInput) this.$refs.imageInput.value = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    onPickImage(e) {
      const f = e.target.files && e.target.files[0];
      if (!f) return;
      if (!f.type.startsWith("image/")) {
        this.$swal && this.$swal({ icon: "warning", title: this.use_language.select_image_file });
        return;
      }
      this.pendingImage = f;
    },
    onPickFile(e) {
      const f = e.target.files && e.target.files[0];
      if (f) this.pendingFile = f;
    },

    insertEmoji(e) {
      this.text = (this.text || "") + e;
    },

    onPickReply(content) {
      // append (ถ้ามีข้อความอยู่แล้ว เคาะ space คั่น)
      this.text = (this.text ? this.text + " " : "") + (content || "");
    },
  },
};
</script>

<style scoped>
.chat-input {
  background: #fff;
  border-top: 1px solid #e0e0e0;
}
.emoji-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 2px;
  max-width: 320px;
}
.emoji-cell {
  font-size: 18px;
  text-align: center;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
}
.emoji-cell:hover {
  background: #f0f0f0;
}
</style>